/**
 * Phone search-bar viewport policy.
 *
 * The composer capsule grows in place on focus. Nothing else moves until a
 * real keyboard has shrunk the visual viewport: then the whole search block
 * is LIFTED (a CSS transform, see text-area-search-bar.tsx) so the composer
 * sits where it reads best in the leftover strip, and lowered again when the
 * keyboard leaves. No keyboard — Chrome device-mode, a hardware keyboard —
 * means no move.
 *
 * The page is not scrolled to do this. Scripted scrolling under a phone
 * keyboard reaches the screen a few frames late on iOS and reads back stale
 * in between, and the browser has its own opinion of where a focused field
 * belongs; a lift has nothing to read back and nothing to argue with.
 *
 * Positions are DOCUMENT coordinates throughout (a client rect minus the
 * document element's own rect; `visualViewport.pageTop` for the visible
 * strip), so they mean the same thing in every engine.
 *
 * Pure functions with no React import so they can be unit-tested under the
 * repo's node-environment jest.
 */

/** visualViewport must shrink by more than this vs innerHeight to count as a keyboard. */
export const KEYBOARD_OPEN_THRESHOLD_PX = 120;

/** Floor for the "already close enough" dead zone, in CSS pixels. */
export const CENTERING_DEAD_ZONE_MIN_PX = 72;

/** Dead zone as a fraction of the visible (keyboard-free) height. */
export const CENTERING_DEAD_ZONE_RATIO = 0.15;

export const isKeyboardOpen = (layoutHeight: number, visualHeight: number): boolean =>
  layoutHeight - visualHeight > KEYBOARD_OPEN_THRESHOLD_PX;

export const centeringDeadZonePx = (visualHeight: number): number =>
  Math.max(CENTERING_DEAD_ZONE_MIN_PX, visualHeight * CENTERING_DEAD_ZONE_RATIO);

/**
 * How much of the layout viewport the keyboard covers. A pinch-zoomed page
 * also has a short visual viewport; that is not a keyboard.
 */
export const keyboardInsetPx = (layoutHeight: number, visualHeight: number, scale = 1): number =>
  Math.abs(scale - 1) >= 0.01 ? 0 : Math.max(0, layoutHeight - visualHeight);

/** Clear air between the composer and the keyboard's top edge. */
export const KEYBOARD_GAP_PX = 12;

/** Clear air between the sticky header and the top of the conversation card. */
export const CARD_HEADER_GAP_PX = 8;

/** A card top shorter than this above the composer row is only the card's own border. */
const CARD_TOP_MIN_PX = 8;

/**
 * First move after a keyboard comes up: nearer than this already reads as in
 * place. Wider than the ~25px between the textarea's centre (what a browser's
 * own reveal aims at) and the composer row's (what we aim at), so a phone
 * that centred the field itself is not nudged a second time.
 */
export const FIRST_PLACEMENT_DEAD_ZONE_PX = 40;

export type KeyboardCompositionInput = {
  /** Height of the keyboard-free strip (`visualViewport.height`). */
  visualHeight: number;
  /** Composer row (textarea + footer). */
  rowTop: number;
  rowHeight: number;
  /** Top of the whole card. Equals `rowTop` until a conversation region sits above the row. */
  cardTop: number;
  /** Height of the sticky header (the document's `scroll-padding-top`). */
  headerBand: number;
};

/**
 * Where the top of the visible strip would have to be, against the document
 * as it is laid out, for the composer to sit right while the keyboard is up.
 * The search block is lifted by the difference between this and where the
 * strip really is.
 *
 * Empty bar: the composer row dead centre in the strip. With a question on the
 * card, centring the row would push the question being answered under the
 * header, so the whole card is centred between header and keyboard instead;
 * a card too tall for that rests its composer just above the keyboard and
 * lets its top run under the header.
 */
export const keyboardCompositionTop = (s: KeyboardCompositionInput): number => {
  const rowBottom = s.rowTop + s.rowHeight;
  if (s.rowTop - s.cardTop <= CARD_TOP_MIN_PX) return s.rowTop + s.rowHeight / 2 - s.visualHeight / 2;
  const bandTop = s.headerBand + CARD_HEADER_GAP_PX;
  const bandHeight = s.visualHeight - bandTop - KEYBOARD_GAP_PX;
  const cardHeight = rowBottom - s.cardTop;
  if (cardHeight <= bandHeight) return s.cardTop - bandTop - (bandHeight - cardHeight) / 2;
  return rowBottom + KEYBOARD_GAP_PX - s.visualHeight;
};

export type KeyboardPlacementInput = KeyboardCompositionInput & {
  /**
   * Top of the visible strip now (`visualViewport.pageTop`), plus the lift
   * already applied: lifting the block by N is, for the composition, the same
   * as the strip sitting N lower.
   */
  visualTop: number;
  /**
   * First placement since this keyboard came up. Later ones (keyboard swap,
   * rotation, a suggestion strip toggling) keep the wide dead zone, so the
   * page does not shuffle under someone who is typing.
   */
  first: boolean;
};

/**
 * How much further the block has to rise for the composer to sit right above
 * the keyboard. 0 = leave it alone: close enough, with the row fully in view.
 * A row the keyboard is covering always moves.
 */
export const keyboardPlacementDelta = (s: KeyboardPlacementInput): number => {
  const delta = keyboardCompositionTop(s) - s.visualTop;
  const rowInView =
    s.rowTop >= s.visualTop && s.rowTop + s.rowHeight + KEYBOARD_GAP_PX <= s.visualTop + s.visualHeight;
  const deadZone = s.first ? FIRST_PLACEMENT_DEAD_ZONE_PX : centeringDeadZonePx(s.visualHeight);
  return rowInView && Math.abs(delta) <= deadZone ? 0 : delta;
};

/**
 * The lift and its way back down: one compositor transition, so it runs at
 * the screen's own rate with nothing scripted per frame. The curve leaves
 * fast and lands long, the way the keyboard it travels with does.
 */
export const KEYBOARD_LIFT_MS = 400;
export const KEYBOARD_LIFT_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Once a lift has landed the composer's place is checked again: this long
 * after the transition, and a miss smaller than this is left alone.
 */
export const LIFT_CHECK_AFTER_MS = 120;
export const LIFT_CHECK_MIN_PX = 8;

/** The keyboard reports its size in steps; a re-placement waits this long after the last one. */
export const KEYBOARD_SETTLE_MS = 250;

/** A "closed" reading under a focused field must hold this long (Android blips while opening). */
export const KEYBOARD_CLOSED_HOLD_MS = 150;

/** The page moved this far without us: the browser revealed the field itself, or a finger is scrolling. */
export const FOREIGN_SCROLL_MIN_PX = 4;

/** A finger that travels this far is scrolling, not tapping. */
export const GESTURE_SCROLL_PX = 10;

/** A visual viewport that got taller is a keyboard on its way out: never a reason to place the composer. */
export const isKeyboardLeaving = (previousVisualHeight: number | null, visualHeight: number): boolean =>
  previousVisualHeight !== null && visualHeight > previousVisualHeight;

/** The keyboard must change size by more than this for another placement to be allowed. */
export const RECENTER_HEIGHT_DELTA_PX = 24;

/**
 * One placement per keyboard geometry. The browser holds its own opinion of
 * where a focused input belongs (scroll-into-view, visual-viewport panning)
 * and expresses it through follow-up scrolls; measuring again after each of
 * those re-triggered our correction and the two fought in an endless
 * center/uncenter loop on real phones. Only a real geometry change (keyboard
 * in/out, rotation, keyboard swap) may re-arm. Fed the keyboard INSET, so a
 * browser toolbar that moves the layout and visual heights together is not a
 * new geometry. `null` = not placed yet.
 */
export const shouldAttemptRecenter = (lastPlacedInset: number | null, inset: number): boolean =>
  lastPlacedInset === null || Math.abs(inset - lastPlacedInset) > RECENTER_HEIGHT_DELTA_PX;

export type ComposerLowerSignals = {
  /** The visual viewport still shows a keyboard. */
  keyboardUp: boolean;
  /** Some text field has focus (the textarea, the budget field, a history edit). */
  editableFocused: boolean;
  /** The image picker, the history panel or dictation has borrowed the focus mid-composition. */
  held: boolean;
};

/**
 * The composer is lifted; may it come down? Always once the viewport says the
 * keyboard is gone — no keyboard, no lift. Before that, as soon as nothing
 * editable has the focus: the keyboard is still sliding away and the composer
 * should travel with it. Not while another field (the budget) still has the
 * keyboard, nor under an interaction that only borrowed the focus.
 */
export const shouldLowerComposer = (s: ComposerLowerSignals): boolean =>
  !s.keyboardUp || (!s.held && !s.editableFocused);

/** Smaller moves than this are not worth a scroll: the conversation is already on screen. */
export const CONVERSATION_RETURN_MIN_PX = 8;

export type ConversationReturnInput = {
  /** `window.scrollY` now. */
  scrollY: number;
  /**
   * Viewport top of the search block: the landing hero (`#main-search-bar`),
   * or the capsule itself when its conversation region overhangs the block.
   */
  blockTop: number;
  /** The document's `scroll-padding-top` — the room the sticky header takes. */
  scrollPaddingTop: number;
};

/**
 * Where the page should go once the shelf under the bar is gone for the rest
 * of this turn: back to the composition a search opens with — the
 * conversation above the composer, in view under the header. On the landing
 * page that is the very top. Only ever UP: a shopper who is above the block
 * (reading the question) is never pulled down. Null = leave the page alone.
 */
export const conversationReturnScrollTop = (s: ConversationReturnInput): number | null => {
  const target = Math.max(0, Math.round(s.scrollY + s.blockTop - s.scrollPaddingTop));
  return s.scrollY - target > CONVERSATION_RETURN_MIN_PX ? target : null;
};

/** Clear air between the header and the top of the picks the "Might interest you" bar scrolls to. */
export const SHELF_SCROLL_GAP_PX = 12;

export type ShelfScrollInput = {
  /** `window.scrollY` now. */
  scrollY: number;
  /** Viewport top of the picks section as it is drawn now. */
  shelfTop: number;
  /**
   * The search block's own lift (its transform's Y; negative while a keyboard
   * has it raised). The bar blurs the composer, so by the time the page lands
   * the block is back down and the lift must not count.
   */
  blockShift: number;
  /** The document's `scroll-padding-top` — the room the sticky header takes. */
  headerBand: number;
};

/** Where the window has to go for the picks to sit just under the header. Never above the top. */
export const shelfScrollTarget = (s: ShelfScrollInput): number =>
  Math.max(0, Math.round(s.scrollY + s.shelfTop - s.blockShift - s.headerBand - SHELF_SCROLL_GAP_PX));
